import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Power, 
  Clock, 
  AlertTriangle, 
  Shield,
  Timer,
  Loader2,
  RefreshCw,
  Plus,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const MaintenanceModeSettings = () => {
  const { api, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [extending, setExtending] = useState(false);
  
  // Form state
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("We're currently performing scheduled maintenance to improve your experience. Please check back soon.");
  const [durationType, setDurationType] = useState('preset'); // 'preset', 'custom', 'indefinite'
  const [presetDuration, setPresetDuration] = useState('30');
  const [customHours, setCustomHours] = useState('');
  const [customMinutes, setCustomMinutes] = useState('');
  const [extendMinutes, setExtendMinutes] = useState('30');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/maintenance/status');
      setStatus(res.data);
      setEnabled(res.data.enabled);
      if (res.data.message) {
        setMessage(res.data.message);
      }
    } catch (err) {
      console.error('Failed to fetch maintenance status:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    // Poll every 15 seconds when maintenance is enabled
    const interval = setInterval(() => {
      if (status?.enabled) {
        fetchStatus();
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchStatus, status?.enabled]);

  const handleToggle = async () => {
    setSaving(true);
    try {
      // Calculate duration in minutes
      let durationMinutes = null;
      
      if (!enabled) {
        // Turning ON maintenance
        if (durationType === 'preset') {
          durationMinutes = parseInt(presetDuration);
        } else if (durationType === 'custom') {
          const hours = parseInt(customHours) || 0;
          const minutes = parseInt(customMinutes) || 0;
          durationMinutes = (hours * 60) + minutes;
        }
        // 'indefinite' leaves durationMinutes as null
      }

      const res = await api.post('/maintenance/toggle', {
        enabled: !enabled,
        message: message,
        duration_minutes: durationMinutes
      });

      setStatus(res.data);
      setEnabled(res.data.enabled);
      
      if (res.data.enabled) {
        toast.success('Maintenance mode ENABLED', {
          description: res.data.end_time 
            ? `Will auto-disable at ${new Date(res.data.end_time).toLocaleString()}`
            : 'Indefinite until manually turned off',
          duration: 5000
        });
      } else {
        toast.success('Maintenance mode DISABLED', {
          description: 'Platform is now accessible to all users'
        });
      }
    } catch (err) {
      toast.error('Failed to toggle maintenance mode');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleExtend = async () => {
    setExtending(true);
    try {
      const minutes = parseInt(extendMinutes);
      if (!minutes || minutes <= 0) {
        toast.error('Please enter a valid number of minutes');
        return;
      }

      const res = await api.post(`/maintenance/extend?additional_minutes=${minutes}`);
      setStatus(res.data);
      
      toast.success(`Extended by ${minutes} minutes`, {
        description: `New end time: ${new Date(res.data.end_time).toLocaleString()}`
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to extend maintenance');
    } finally {
      setExtending(false);
    }
  };

  // Format remaining time for display
  const formatRemainingTime = (seconds) => {
    if (!seconds) return null;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  if (user?.role !== 'admin') {
    return null;
  }

  if (loading) {
    return (
      <Card className="border-2 border-red-500/20">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 ${enabled ? 'border-red-500/50 bg-red-500/5' : 'border-red-500/20'}`}>
      <CardHeader className={`${enabled ? 'bg-red-500/10' : 'bg-gradient-to-r from-red-500/5 to-orange-500/5'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${enabled ? 'bg-red-500' : 'bg-red-500/20'}`}>
              <Power className={`h-5 w-5 ${enabled ? 'text-white' : 'text-red-500'}`} />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                Maintenance Mode
                {enabled && (
                  <Badge className="bg-red-500 text-white animate-pulse">
                    ACTIVE
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Temporarily restrict platform access to admins only
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchStatus}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {/* Status Display when Active */}
        {enabled && status && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span className="font-semibold text-red-600 dark:text-red-400">
                  Maintenance is currently ACTIVE
                </span>
              </div>
              {status.remaining_seconds && (
                <Badge className="bg-amber-500 text-white font-mono">
                  <Timer className="h-3 w-3 mr-1" />
                  {formatRemainingTime(status.remaining_seconds)}
                </Badge>
              )}
            </div>
            
            {status.end_time && (
              <p className="text-sm text-muted-foreground">
                Scheduled to end: <span className="font-medium">{new Date(status.end_time).toLocaleString()}</span>
              </p>
            )}
            
            {!status.end_time && (
              <p className="text-sm text-amber-600">
                Running indefinitely until manually turned off
              </p>
            )}

            {status.started_by && (
              <p className="text-xs text-muted-foreground">
                Started by {status.started_by} at {new Date(status.started_at).toLocaleString()}
              </p>
            )}

            {/* Extend Timer */}
            {status.end_time && (
              <div className="flex items-center gap-2 pt-2 border-t border-red-500/20">
                <Input
                  type="number"
                  value={extendMinutes}
                  onChange={(e) => setExtendMinutes(e.target.value)}
                  className="w-24"
                  placeholder="30"
                  min="1"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExtend}
                  disabled={extending}
                  className="ml-auto"
                >
                  {extending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1" />
                      Extend
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Message Configuration */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            Maintenance Message
            <Badge variant="outline" className="text-xs">Shown to users</Badge>
          </Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="We're currently performing scheduled maintenance..."
            rows={3}
            className="resize-none"
            data-testid="maintenance-message-input"
          />
          <p className="text-xs text-muted-foreground">
            This message will be displayed to non-admin users during maintenance
          </p>
        </div>

        <Separator />

        {/* Duration Configuration - Only show when not currently enabled */}
        {!enabled && (
          <div className="space-y-4">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Maintenance Duration
            </Label>

            {/* Duration Type Selection */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setDurationType('preset')}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  durationType === 'preset'
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                    : 'border-muted hover:border-primary/50'
                }`}
              >
                <Timer className="h-4 w-4 mx-auto mb-1" />
                Preset
              </button>
              <button
                onClick={() => setDurationType('custom')}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  durationType === 'custom'
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                    : 'border-muted hover:border-primary/50'
                }`}
              >
                <Clock className="h-4 w-4 mx-auto mb-1" />
                Custom
              </button>
              <button
                onClick={() => setDurationType('indefinite')}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  durationType === 'indefinite'
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                    : 'border-muted hover:border-primary/50'
                }`}
              >
                <AlertTriangle className="h-4 w-4 mx-auto mb-1" />
                Indefinite
              </button>
            </div>

            {/* Preset Duration */}
            {durationType === 'preset' && (
              <div className="space-y-2">
                <Label>Select Duration</Label>
                <Select value={presetDuration} onValueChange={setPresetDuration}>
                  <SelectTrigger data-testid="preset-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                    <SelectItem value="360">6 hours</SelectItem>
                    <SelectItem value="720">12 hours</SelectItem>
                    <SelectItem value="1440">24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Custom Duration */}
            {durationType === 'custom' && (
              <div className="flex items-center gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Hours</Label>
                  <Input
                    type="number"
                    value={customHours}
                    onChange={(e) => setCustomHours(e.target.value)}
                    placeholder="0"
                    min="0"
                    max="72"
                    className="w-20"
                    data-testid="custom-hours-input"
                  />
                </div>
                <span className="text-muted-foreground mt-6">:</span>
                <div className="space-y-1">
                  <Label className="text-xs">Minutes</Label>
                  <Input
                    type="number"
                    value={customMinutes}
                    onChange={(e) => setCustomMinutes(e.target.value)}
                    placeholder="30"
                    min="0"
                    max="59"
                    className="w-20"
                    data-testid="custom-minutes-input"
                  />
                </div>
              </div>
            )}

            {/* Indefinite Warning */}
            {durationType === 'indefinite' && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-500 inline mr-2" />
                <span className="text-amber-700 dark:text-amber-300">
                  Maintenance will run until you manually turn it off
                </span>
              </div>
            )}
          </div>
        )}

        <Separator />

        {/* Toggle Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {enabled ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
              </p>
              <p className="text-sm text-muted-foreground">
                {enabled 
                  ? 'Platform will be accessible to all users'
                  : 'Only admins will be able to access the platform'
                }
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleToggle}
            disabled={saving}
            className={enabled 
              ? 'bg-emerald-500 hover:bg-emerald-600' 
              : 'bg-red-500 hover:bg-red-600'
            }
            data-testid="maintenance-toggle-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : enabled ? (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            ) : (
              <XCircle className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Processing...' : enabled ? 'Go Live' : 'Shutdown'}
          </Button>
        </div>

        {/* Info Box */}
        <div className="p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
          <p className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span>Admins can always access the platform during maintenance</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MaintenanceModeSettings;
